import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { TextArea } from '@delmonicos/shared-design-system-react/src/components/text-area/text-area.component';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useCreateTicketFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tickets/models/inputs/create-ticket.form-validation';
import { TransportType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/core/src/enums/transport.type';
import { TicketNotificationContext } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/enums/ticket-notification-context.enum';
import { CreateTicketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/inputs/create-ticket.model';
import {
  Controller,
  RegisterOptions,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AssigneeSelectionComponent } from '../../shared/assignee-selection/assignee-selection.component';
import { PrioritySelectionComponent } from '../../shared/priority-selection/priority-selection.component';
import { RelatedResourcesSectionController } from '../../shared/related-resources-section/related-resources-section.controller';

const TicketCreateFormStep1NotificationContactsSection =
  (): React.JSX.Element => {
    const { t } = useTranslation();
    const { control } = useFormContext<CreateTicketModel>();
    const { notificationContactsContact } = useCreateTicketFormValidation();
    const { fields, append, remove } = useFieldArray({
      control,
      name: 'notificationContacts',
    });

    return (
      <Stack direction="column" gap={GapEnum.S}>
        <Stack direction="row" gap={GapEnum.M} alignItems="center">
          <span>
            {t(
              'delmonicos-shared-sdk.inputs-create-ticket.notification-contacts',
            )}
          </span>
          <ButtonIcon
            type="button"
            icon="plus"
            onClick={() =>
              append({
                contact: '',
                type: TransportType.Email,
                contexts: Object.values(TicketNotificationContext),
              })
            }
          />
        </Stack>
        {fields.map((field, index) => (
          <Stack
            key={field.id}
            direction="row"
            gap={GapEnum.S}
            alignItems="flex-end">
            <Controller
              name={`notificationContacts.${index}.contact`}
              control={control}
              rules={
                notificationContactsContact as RegisterOptions<
                  CreateTicketModel,
                  any
                >
              }
              render={({ field: contactField, fieldState: { error } }) => (
                <TextInput
                  {...contactField}
                  label=""
                  placeholder={t(
                    'delmonicos-shared-sdk.inputs-create-ticket.notification-contacts',
                  )}
                  errorMessage={error?.message}
                />
              )}
            />
            <ButtonIcon
              type="button"
              icon="trash-01"
              onClick={() => remove(index)}
            />
          </Stack>
        ))}
      </Stack>
    );
  };

export const TicketCreateFormStep1Controller = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { control } = useFormContext<CreateTicketModel>();
  const { title, description } = useCreateTicketFormValidation();

  return (
    <Stack gap={GapEnum.L} flex={1} direction="column">
      <Card direction="column" gap={GapEnum.L}>
        <AccentTitle
          icon="terminal"
          text={t('$.ticket-create.card-association')}
        />
        <RelatedResourcesSectionController />
      </Card>
      <Card direction="column" gap={GapEnum.L} flex={1}>
        <AccentTitle
          icon="info-circle"
          text={t('$.ticket-create.card-informations')}
          variant={AccentVariantEnum.Accent1}
        />
        <Controller
          name="priority"
          control={control}
          render={PrioritySelectionComponent}
        />
        <Controller
          name="title"
          control={control}
          rules={title}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.inputs-create-ticket.title')}
              placeholder={t(
                'delmonicos-shared-sdk.inputs-create-ticket.title',
              )}
              required={!!title.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          rules={description}
          render={({ field, fieldState: { error } }) => (
            <TextArea
              label={t(
                'delmonicos-shared-sdk.inputs-create-ticket.description',
              )}
              placeholder=""
              required={!!description.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
        <Controller
          name="assigneeId"
          control={control}
          render={AssigneeSelectionComponent}
        />
        <TicketCreateFormStep1NotificationContactsSection />
      </Card>
    </Stack>
  );
};
